import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '';

const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * 固定人格の型定義
 */
export interface IdolPersonaMaster {
  id: string;
  character_key: string;
  character_name: string;
  system_prompt_base: string;
  language: string;
  created_at: string;
  updated_at: string;
}

/**
 * 本人カスタム設定の型定義
 */
export interface IdolPersonaSettings {
  id: string;
  character_key: string;
  tone_style: string;
  healing_level: number;
  romantic_level: number;
  teasing_level: number;
  emoji_level: number;
  name_call_frequency: number;
  question_frequency: number;
  max_intimacy_level: number;
  response_length: string;
  memory_style: string;
  topic_strengths: string[];
  avoid_topics: string[];
  backchannel_style?: string;
  question_depth?: string;
  initiative_level?: string;
  topic_shift_frequency?: string;
  encouragement_strength?: string;
  updated_at: string;
}

/**
 * Supabaseからペルソナ設定を管理するカスタムフック
 */
export function useSupabasePersona(characterKey: string = 'inochan') {
  const [master, setMaster] = useState<IdolPersonaMaster | null>(null);
  const [settings, setSettings] = useState<IdolPersonaSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * 固定人格を取得
   */
  const fetchMaster = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('idol_persona_master')
        .select('*')
        .eq('character_key', characterKey)
        .maybeSingle();

      if (fetchError) throw fetchError;
      setMaster(data);
      return data;
    } catch (err) {
      console.error('固定人格の取得に失敗しました:', err);
      setError(err instanceof Error ? err.message : '不明なエラー');
      return null;
    }
  }, [characterKey]);

  /**
   * 本人カスタム設定を取得
   */
  const fetchSettings = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('idol_persona_settings')
        .select('*')
        .eq('character_key', characterKey)
        .maybeSingle();

      if (fetchError) throw fetchError;
      setSettings(data);
      return data;
    } catch (err) {
      console.error('カスタム設定の取得に失敗しました:', err);
      setError(err instanceof Error ? err.message : '不明なエラー');
      return null;
    }
  }, [characterKey]);

  /**
   * 本人カスタム設定を更新
   */
  const updateSettings = useCallback(async (
    updates: Partial<Omit<IdolPersonaSettings, 'id' | 'character_key' | 'updated_at'>>
  ): Promise<boolean> => {
    try {
      const { error: updateError } = await supabase
        .from('idol_persona_settings')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('character_key', characterKey);

      if (updateError) throw updateError;

      // 更新後に再取得
      await fetchSettings();
      return true;
    } catch (err) {
      console.error('カスタム設定の更新に失敗しました:', err);
      setError(err instanceof Error ? err.message : '不明なエラー');
      return false;
    }
  }, [characterKey, fetchSettings]);

  /**
   * 本人カスタム設定をリセット（デフォルト値に戻す）
   */
  const resetSettings = useCallback(async (): Promise<boolean> => {
    try {
      const defaultSettings = {
        tone_style: 'やわらかめ',
        healing_level: 5,
        romantic_level: 3,
        teasing_level: 2,
        emoji_level: 1,
        name_call_frequency: 3,
        question_frequency: 2,
        max_intimacy_level: 4,
        response_length: 'short',
        memory_style: 'natural',
        topic_strengths: ['恋愛相談', '仕事の悩み', '日常雑談', '応援'],
        avoid_topics: ['露骨な性的内容', '暴力的内容'],
        updated_at: new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from('idol_persona_settings')
        .update(defaultSettings)
        .eq('character_key', characterKey);

      if (updateError) throw updateError;

      // 更新後に再取得
      await fetchSettings();
      return true;
    } catch (err) {
      console.error('カスタム設定のリセットに失敗しました:', err);
      setError(err instanceof Error ? err.message : '不明なエラー');
      return false;
    }
  }, [characterKey, fetchSettings]);

  /**
   * 初回読み込み
   */
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      await Promise.all([fetchMaster(), fetchSettings()]);
      setIsLoading(false);
    };

    loadData();
  }, [fetchMaster, fetchSettings]);

  return {
    master,
    settings,
    isLoading,
    error,
    fetchMaster,
    fetchSettings,
    updateSettings,
    resetSettings,
  };
}